import type {ReactElement, ReactNode} from 'react';
import React from 'react';
import styles from './ai-chat-history-drawer.module.scss';
import {Drawer} from '@Components/drawer/drawer';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {Direction, DrawerSize} from '@Components/drawer/drawer.types';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {ChatHistoryList} from '@/ai-chat/components/chat-history-list/chat-history-list';
import {ChatHistorySearch} from '@/ai-chat/components/chat-history-search/chat-history-search';
import {AiChatSidebarNavItem} from '@/ai-chat/components/ai-chat-sidebar-nav-item/ai-chat-sidebar-nav-item';
import {AiChatLogo} from '@/ai-chat/components/ai-chat-logo/ai-chat-logo';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import {ProgressiveBlurOverlay} from '@Components/progressive-blur-overlay/progressive-blur-overlay';
import {getAiChatBasePath} from '@/ai-chat/lib/chat-history-library';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {useAiChatLayout} from '@AiChat/hooks/useAiChatLayout';
import type {AiChatHistoryDrawerProps} from './ai-chat-history-drawer.types';

export function AiChatHistoryDrawer({coversHost = false}: AiChatHistoryDrawerProps): ReactElement {
  const {actions, state} = useAiChat();
  const {isSmall} = useAiChatSize();
  const {floatsSidebarOverChat} = useAiChatLayout();
  const historyScrollContainer = React.useRef<HTMLDivElement>(null);
  const {topShadow} = useTopBottomScrollShadow(historyScrollContainer);

  const getPositioningClassName = (): string => {
    if (coversHost) {
      return styles.sideDrawerCoveringHost;
    }

    if (floatsSidebarOverChat) {
      return '';
    }

    return styles.sideDrawerInFlow;
  };

  const getHomepageClassName = (): string => {
    if (floatsSidebarOverChat || state.currentChatId !== undefined) {
      return '';
    }

    return styles.isHomepageDrawer;
  };

  const getCollapseClassName = (): string => {
    if (!state.isEmbedded || coversHost) {
      return '';
    }

    return styles.collapsesInPlace;
  };

  const handleOnClose = (): void => {
    actions.setIsSidebarExpanded(false);
  };

  /** A phone's drawer covers the view it opens, so it closes; a desktop's stays as it is. */
  const openFromDrawer = (navigate: () => void): (() => void) => {
    return (): void => {
      navigate();
      actions.setIsSidebarExpanded(!isSmall);
    };
  };

  const getTooltip = (title: string): OverlayChildren => {
    return (
      <div className={`spacing-p-t-1 spacing-p-b-1 spacing-p-l-2 spacing-p-r-2 radius-8 ${styles.tooltipContainer}`}>
        <Text size={TextSize.XSMALL} className={styles.tooltipText} val={title} />
      </div>
    );
  };

  const getHistoryDrawerTrigger = (): ReactElement => {
    return (
      <OverlayTrigger
        defaultShow={false}
        trigger={['hover', 'focus']}
        overlay={getTooltip(window.i18next.t('pmwjs_close_sidebar'))}
        delay={{show: 100, hide: 100}}
        placement="right"
        offset={[0, 12]}
      >
        <Icon
          className={`radius-8`}
          onClick={() => {
            actions.setIsSidebarExpanded(false);
          }}
          icon="icon-sidebar"
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.SQUARE}
          type={IconType.GHOST}
          ariaLabel={window.i18next.t('pmwjs_close_sidebar')}
          isUsedAsButton
        />
      </OverlayTrigger>
    );
  };

  const getNewChatDrawerItem = (): ReactElement => {
    return (
      <AiChatSidebarNavItem
        icon="icon-plus-bold"
        hasCircledIcon
        label={window.i18next.t('pmwjs_new_chat')}
        href={getAiChatBasePath()}
        onClick={(e) => {
          e.preventDefault();
          actions.activateForNewChat();
          actions.setCurrentChatId(undefined);
          actions.setIsSidebarExpanded(!isSmall);
        }}
      />
    );
  };

  const getAssetsDrawerItem = (): ReactElement | null => {
    if (!state.hasGeneratedAssets) {
      return null;
    }

    return (
      <AiChatSidebarNavItem
        icon="icon-designs"
        label={window.i18next.t('pmwjs_my_ai_designs')}
        onClick={openFromDrawer(actions.navigateToAssets)}
      />
    );
  };

  const getSearchDrawerItem = (): ReactElement => {
    if (state.isChatHistorySearchOpen) {
      return <ChatHistorySearch />;
    }

    return (
      <AiChatSidebarNavItem icon="icon-search" label={window.i18next.t('pmwjs_search')} onClick={actions.openChatHistorySearch} />
    );
  };

  const getDrawerLogoAreaContent = (): ReactElement => {
    if (isSmall) {
      return <Text size={TextSize.SMALL} bold val={window.i18next.t('pmwjs_all_chats')} />;
    }

    return <AiChatLogo showHamburgerMenu={false} />;
  };

  const getSidebarHeader = (): ReactNode => {
    if (coversHost) {
      return null;
    }

    return (
      <div className={`flex-row-align-center ${styles.sidebarHeader} ${isSmall ? 'spacing-p-2 spacing-p-l-4' : 'spacing-p-1 spacing-p-l-3'}`}>
        {getDrawerLogoAreaContent()}
        {getHistoryDrawerTrigger()}
      </div>
    );
  };

  const getDrawerTopContent = (): ReactElement => {
    return (
      <>
        {getSidebarHeader()}
        <div className={`spacing-p-l-1 spacing-p-r-1 spacing-p-t-2 flex-v-row gap-1 ${isSmall ? 'spacing-p-b-5' : 'spacing-p-b-4'} ${topShadow ? styles.bottomShadow : ''}`}>
          {getNewChatDrawerItem()}
          {getAssetsDrawerItem()}
          {getSearchDrawerItem()}
        </div>
      </>
    );
  };

  const getBottomPaddingClassnameToAccountForBottomButtons = (): string => {
    if (coversHost) {
      return '';
    }

    return state.isMemoryEnabled ? 'spacing-p-b-20' : 'spacing-p-b-12';
  };

  const getMemoryButton = (): ReactNode => {
    if (!state.isMemoryEnabled) {
      return null;
    }

    return <Button onClick={openFromDrawer(actions.navigateToMemory)} icon="icon-book" text={window.i18next.t('pmwjs_ai_memory')} isFullWidth type={ButtonType.SECONDARY} size={ButtonSize.MEDIUM} />;
  };

  const getDrawerBottomButtons = (): ReactNode => {
    if (coversHost) {
      return null;
    }

    return (
      <div className={`_full-width spacing-p-l-3 spacing-p-r-3 spacing-p-b-3 spacing-p-t-6 flex-v-row gap-2 ${styles.quickTipsContainer}`}>
        <ProgressiveBlurOverlay className={styles.progressiveBlurOverlay} />
        {getMemoryButton()}
        <Button onClick={openFromDrawer(actions.navigateToQuickTips)} icon="icon-bulb" text={window.i18next.t('pmwjs_quick_tips')} isFullWidth type={ButtonType.TERTIARY} size={ButtonSize.MEDIUM} />
      </div>
    );
  };

  return (
    <Drawer
      size={DrawerSize.SMALL}
      isOpen={state.isSidebarExpanded}
      onClose={handleOnClose}
      overlay={floatsSidebarOverChat && !coversHost}
      overlayClassName={styles.sideDrawerOverlay}
      direction={Direction.LEFT}
      hasOverflowHidden={false}
      className={`${getBottomPaddingClassnameToAccountForBottomButtons()} ${getHomepageClassName()} ${getCollapseClassName()} ${styles.sideDrawer} ${getPositioningClassName()} flex-shrink-0`}
    >
      {getDrawerTopContent()}
      <ChatHistoryList containerRef={historyScrollContainer} />
      {getDrawerBottomButtons()}
    </Drawer>
  );
}
